import { type ComponentPropsWithRef, useContext, useEffect, useRef } from 'react';
import { animated, to, useSpring } from '@react-spring/web';
import { useDrag, type DragState } from '@use-gesture/react';
import { GridContext } from './GridContext';
import { GridItemContext } from './GridItemContext';
type SpringValues = {
  x: number;
  y: number;
  immediate: boolean;
  zIndex: string;
  scale: number;
  opacity: number;
};
type ElementProps = ComponentPropsWithRef<typeof animated.div>;
interface GridItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  children: (({
    index
  }: {
    index: number;
  }) => React.ReactNode) | React.ReactNode;
  disabled?: boolean;
}
export function GridItem({
  children,
  style,
  className,
  disabled = false,
  ...other
}: GridItemProps) {
  const gridItemContext = useContext(GridItemContext);
  const gridContext = useContext(GridContext);
  if (!gridItemContext) {
    throw Error('Unable to find GridItem gridItemContext. Please ensure that GridItem is used as a child of GridDropZone');
  }
  const {
    top,
    disableDrag,
    endTraverse,
    onStart,
    mountWithTraverseTarget,
    left,
    onMove,
    onEnd,
    grid,
    dragging,
    i: index
  } = gridItemContext;
  const {
    isRtl,
    vertical,
    horizontal,
    scale
  } = gridContext;
  const {
    columnWidth,
    rowHeight
  } = grid;
  const startCoords = useRef<[number, number]>([left, top]);
  const [styles, set] = useSpring<SpringValues>(() => {
    if (mountWithTraverseTarget) {
      const [x, y] = mountWithTraverseTarget;
      endTraverse();
      return {
        x,
        y,
        immediate: true,
        zIndex: '1',
        scale,
        opacity: 0.8
      };
    }
    return {
      x: left,
      y: top,
      immediate: true,
      zIndex: '0',
      scale: 1,
      opacity: 1
    };
  });
  const handleStart = () => {
    startCoords.current = [(isRtl ? -1 : 1) * left, top];
    onStart?.();
  };
  const handleMove = (state: DragState) => {
    const x = (isRtl ? -1 : 1) * (startCoords.current[0] + state.movement[0]);
    const y = startCoords.current[1] + state.movement[1];
    set({
      x,
      y,
      zIndex: '1',
      immediate: true,
      opacity: 0.8,
      scale
    });
    onMove(state, x, y);
  };
  const handleEnd = (state: DragState) => {
    const x = (isRtl ? -1 : 1) * (startCoords.current[0] + state.movement[0]);
    const y = startCoords.current[1] + state.movement[1];
    onEnd(state, x, y);
  };
  const bind = useDrag(state => {
    const {
      first,
      last,
      cancel
    } = state;
    if (disableDrag || disabled) {
      cancel?.();
      return;
    }
    if (first) {
      handleStart();
    } else if (last) {
      handleEnd(state);
    } else {
      handleMove(state);
    }
  }, {
    pointer: {
      touch: true
    },
    eventOptions: {
      passive: true
    }
  });
  useEffect(() => {
    if (!dragging) {
      set({
        x: left,
        y: top,
        zIndex: '0',
        opacity: 1,
        scale: 1,
        immediate: false
      });
    }
  }, [dragging, left, top, set]);
  const props: ElementProps = {
    className: `GridItem${dragging ? ' dragging' : ''}${disableDrag ? ' disabled' : ''}${className ? ' ' + className : ''}`,
    style: {
      zIndex: styles.zIndex,
      opacity: styles.opacity,
      transform: to([styles.x, styles.y, styles.scale], (x, y, currentScale) => {
        const transformScale = `scale(${currentScale})`;
        if (horizontal && vertical) {
          return `translate3d(${(isRtl ? -1 : 1) * x}px, ${y}px, 0) ${transformScale}`;
        } else if (horizontal) {
          return `translateX(${(isRtl ? -1 : 1) * x}px) ${transformScale}`;
        } else if (vertical) {
          return `translateY(${y}px) ${transformScale}`;
        }
      }),
      cursor: disableDrag ? 'grab' : undefined,
      position: 'absolute',
      width: `${columnWidth}px`,
      height: `${rowHeight}px`,
      boxSizing: 'border-box',
      ...style
    },
    ...bind(),
    ...other
  };
  return <animated.div {...props} data-sentry-element="animated.div" data-sentry-component="GridItem" data-sentry-source-file="GridItem.tsx">
      {typeof children === 'function' ? children({
      index
    }) : children}
    </animated.div>;
}